/**
 * `second_look.submit_to_decision` — how long a customer waits between a second-look application
 * being submitted and its decision resolving.
 *
 * The other vitals in this directory are render-driven: a provider stamps the clock on mount and a
 * gate reports on paint. This one cannot be, because both boundaries are events rather than
 * renders — the clock starts in a mutation callback and stops in a polling callback — and the
 * workflow changes stage in between. State therefore lives at module scope instead of a React ref,
 * so a stage change cannot discard a measurement that is still in flight.
 */

/** Every second-look path funnels through the same submission and polling hooks. */
export const SECOND_LOOK_SUBMIT_TO_DECISION_VITAL = 'second_look.submit_to_decision';

/**
 * `review` means the lender still had the application under review when the wait ended, which is
 * a different failure from `timeout`, where nothing was ever heard back. Both currently land the
 * customer on the same decline screen. `error` covers both a lender error and a wait torn down
 * before any decision arrived, since neither leaves the customer with an answer. `redirect` means
 * the poll returned a provider redirectUrl and polling stopped.
 */
export type SecondLookDecisionOutcome =
    | 'approved'
    | 'declined'
    | 'error'
    | 'review'
    | 'timeout'
    | 'submit_failed'
    /** Applicant was handed a provider redirectUrl instead of approve/decline. */
    | 'redirect';

interface SecondLookAttempt {
    startedAt: number;
    context: Record<string, unknown>;
}

/** Attempts whose loan id is known. Polling reports against these. */
const attemptsById = new Map<string, SecondLookAttempt>();

/** The attempt between `mutate()` and the response carrying its loan id. */
let pendingAttempt: SecondLookAttempt | null = null;

const emit = (
    attempt: SecondLookAttempt,
    outcome: SecondLookDecisionOutcome,
    financingId?: string,
) => {
    window.DD_RUM?.addDurationVital?.(SECOND_LOOK_SUBMIT_TO_DECISION_VITAL, {
        startTime: attempt.startedAt,
        duration: Date.now() - attempt.startedAt,
        context: {
            ...attempt.context,
            ...(financingId ? { journeyId: financingId } : {}),
            outcome,
        },
    });
};

/**
 * Starts the clock as the submission leaves the client. Repeat calls before the loan id arrives
 * keep the earliest start, so a re-rendered effect cannot shorten the measured wait.
 */
export const startSecondLookAttempt = (context: Record<string, unknown> = {}) => {
    if (pendingAttempt) {
        return;
    }

    pendingAttempt = { startedAt: Date.now(), context };
};

/**
 * Promotes the pending attempt once the submission response names the loan, which is what lets
 * polling find it again. The id doubles as the correlation key across second-look vitals and as
 * the backend loan record's id.
 */
export const identifySecondLookAttempt = (financingId: string) => {
    if (!pendingAttempt) {
        return;
    }

    attemptsById.set(financingId, pendingAttempt);
    pendingAttempt = null;
};

/**
 * Reports the decision a customer waited for. No-ops on an unknown id, which is what keeps the
 * generic polling hook from emitting durations for loans that never went through a second look.
 */
export const reportSecondLookAttempt = (
    financingId: string,
    outcome: SecondLookDecisionOutcome,
) => {
    const attempt = attemptsById.get(financingId);

    if (!attempt) {
        return;
    }

    // Deleted before emitting: polling calls back on every poll that has data, not only the first
    // one to reach a terminal status.
    attemptsById.delete(financingId);
    emit(attempt, outcome, financingId);
};

/** Reports a submission that never produced a loan to wait on. */
export const failSecondLookAttempt = () => {
    if (!pendingAttempt) {
        return;
    }

    const attempt = pendingAttempt;
    pendingAttempt = null;
    emit(attempt, 'submit_failed');
};

/** Module-scoped state outlives a test's render tree, so tests must clear it between cases. */
export const resetSecondLookAttempts = () => {
    attemptsById.clear();
    pendingAttempt = null;
};
